// src/pages/Courses.js
import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import {
  createCourse,
  getCourses,
  deleteCourse,
  updateCourse,
} from "../services/courseService";
import { getSkills } from "../services/skillService";
import { logStudentActivity } from "../services/activityService";
import { useAuth } from "../context/AuthProvider";
import Loader from "../components/Loader";
import { Spinner } from "react-bootstrap";
import { PencilIcon, TrashIcon } from "lucide-react";
import { toast } from "react-toastify";

export default function Courses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    skills: [],
    attachments: [],
  });
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    fetchCourses();
    fetchSkills();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    const response = await getCourses();
    setCourses(response);
    setLoading(false);
  };

  const fetchSkills = async () => {
    const response = await getSkills();
    response.sort((a, b) => a.name.localeCompare(b.name));
    setSkills(response);
  };

  const handleAddCourse = async () => {
    if (!formData.name) return;
    setBtnLoading(true);
    const courseId = await createCourse(formData);
    setBtnLoading(false);
    setCourses([
      ...courses,
      {
        id: courseId,
        name: formData.name,
        description: formData.description,
        skills: formData.skills,
        attachments: formData.attachments,
      },
    ]);
    setFormData({ name: "", description: "", skills: [], attachments: [] });
    setShow(false);
    toast.success("Course added successfully");
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    setLoading(true);

    // Get course name before deletion for activity log
    const course = courses.find((c) => c.id === id);
    const courseName = course?.name || "Unknown course";

    await deleteCourse(id);

    // Log activity
    const activity = {
      userId: user.uid,
      action: "delete_course",
      entityType: "course",
      entityId: id,
      description: `${user.name} deleted course: ${courseName}`,
      metadata: {
        courseName,
      },
    };

    logStudentActivity(activity).catch((err) => console.error(err));

    setCourses(courses.filter((course) => course.id !== id));
    setLoading(false);
    toast.success("Course deleted successfully");
  };

  const handleUpdateCourse = async () => {
    if (!formData.name) return;
    setBtnLoading(true);
    await updateCourse(selectedCourse.id, formData);

    // Log activity
    const activity = {
      userId: user.uid,
      action: "update_course",
      entityType: "course",
      entityId: selectedCourse.id,
      description: `${user.name} updated course: ${formData.name}`,
      metadata: {
        courseName: formData.name,
        skillsCount: formData.skills.length,
      },
    };

    logStudentActivity(activity).catch((err) => console.error(err));

    setBtnLoading(false);
    setCourses(
      courses.map((course) =>
        course.id === selectedCourse.id ? { ...course, ...formData } : course
      )
    );
    setShow(false);
    toast.success("Course updated successfully");
  };

  const handleEditCourse = (course) => {
    setFormData({
      name: course.name,
      description: course.description,
      skills: course.skills,
      attachments: course.attachments,
    });
    setShow(true);
    setSelectedCourse(course);
  };

  const filteredCourses = courses
    .filter((s) =>
      Object.values(s).some((value) =>
        String(value).toLowerCase().includes(filter.toLowerCase())
      )
    )
    .sort((a, b) => {
      const aValue = String(a[sortKey]).toLowerCase();
      const bValue = String(b[sortKey]).toLowerCase();
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredCourses.length / pageSize);
  const paginatedCourses = filteredCourses.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const openAddCourseModal = () => {
    setSelectedCourse(null);
    setFormData({ name: "", description: "", skills: [], attachments: [] });
    setShow(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Courses ({courses.length})</h2>
        <Button onClick={() => openAddCourseModal()}>+ Add Course</Button>
      </div>

      {loading && <Loader />}

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Description</th>
            <th>Skills</th>
            <th>Attachments</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedCourses.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center">
                No courses found
              </td>
            </tr>
          )}
          {paginatedCourses.map((c, idx) => (
            <tr key={c.id}>
              <td>{idx + 1}</td>
              <td>{c.name}</td>
              <td>{c.description}</td>
              <td>{c.skills.map((skill) => skill.name).join(", ")}</td>
              <td>{c.attachments.join(", ")}</td>
              <td>
                <div className="flex space-x-2">
                  <Button variant="info" onClick={() => handleEditCourse(c)}>
                    <PencilIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteCourse(c.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Add Course Modal */}
      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Course</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-red-500">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Enter course name"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Enter description"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Skills <span className="text-red-500">*</span>
            </Form.Label>
            <div className="grid grid-cols-4 gap-2">
              {skills.map((skill) => (
                <Form.Check
                  key={skill.id}
                  type="checkbox"
                  label={skill.name}
                  checked={formData.skills.some((s) => s.id === skill.id)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      skills: e.target.checked
                        ? prev.skills.some((s) => s.id === skill.id)
                          ? prev.skills
                          : [...prev.skills, skill]
                        : prev.skills.filter((s) => s.id !== skill.id),
                    }))
                  }
                />
              ))}
            </div>
            {formData.skills.length === 0 && (
              <div className="text-red-500 text-xs mt-1">
                Please select at least one skill.
              </div>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>Attachments (comma separated)</Form.Label>
            <Form.Control
              type="text"
              value={formData.attachments}
              onChange={(e) =>
                setFormData({ ...formData, attachments: e.target.value })
              }
              placeholder="e.g. syllabus.pdf, notes.docx"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={btnLoading}
            onClick={selectedCourse ? handleUpdateCourse : handleAddCourse}
          >
            {btnLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="text-white"
                />
                <span className="ml-2">
                  {selectedCourse ? "Updating..." : "Saving..."}
                </span>
              </>
            ) : (
              <>{selectedCourse ? "Update" : "Save"}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
